import React, { useState } from "react";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faCaretRight, faCaretDown } from "@fortawesome/pro-solid-svg-icons";
import PropTypes from "prop-types";

import "./nested-list.scss";
import {
  TriCheckBox,
  CheckboxStates,
} from "@transfr-inc/dashboard-components/forms";
import { Badge } from "@transfr-inc/dashboard-components";

const { CHECKED, EMPTY, INDETERMINATE } = CheckboxStates;

const NestedList = ({ item, depth = 0, handleSelect, expandedDepth = 2 }) => {
  const [expanded, setExpanded] = useState(depth < expandedDepth - 1);
  const hasChildren = item.children && item.children.length > 0;

  const toggleExpand = () => {
    if (hasChildren) {
      setExpanded(!expanded);
    }
  };

  const getCheckboxState = (item) => {
    if (!hasChildren) {
      return item.selected ? CHECKED : EMPTY;
    }

    let hasChecked = item.selected;
    let hasUnchecked = !item.selected;

    for (const child of item.children) {
      const childState = getCheckboxState(child);

      if (childState === CHECKED) {
        hasChecked = true;
      } else if (childState === INDETERMINATE) {
        hasChecked = true;
        hasUnchecked = true;
      } else {
        hasUnchecked = true;
      }

      if (hasChecked && hasUnchecked) {
        return INDETERMINATE;
      }
    }

    if (hasChecked && !hasUnchecked) return CHECKED;
    if (!hasChecked && hasUnchecked) return EMPTY;
    return INDETERMINATE; // fallback, shouldn't hit
  };

  const value = getCheckboxState(item);
  const valueOnChange = !(value === CHECKED); // empty and indeterminate go to true on click

  return (
    <div className={`nested-item ${depth > 0 && "margin-top"}`}>
      <div className="collapse-column">
        {hasChildren && (
          <button onClick={toggleExpand} className="toggle-button">
            <FontAwesomeIcon icon={expanded ? faCaretDown : faCaretRight} />
          </button>
        )}
        {!hasChildren && <span className="empty-space" />}
      </div>
      <div className="checkbox-column">
        <TriCheckBox
          value={value}
          onChange={() => handleSelect(item.id, valueOnChange)}
        />
        {hasChildren && expanded && <div className="vertical-line" />}
      </div>
      <div className="content-column">
        <div className="nested-item-name">
          <div className={`${!depth && "header"}`}>{item.name}</div>
          {item.displayTag && <Badge value={item.displayTag} />}
        </div>
        {hasChildren && expanded && (
          <div>
            {item.children.map((child) => (
              <NestedList
                key={child.id}
                item={child}
                depth={depth + 1}
                handleSelect={handleSelect}
              />
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

NestedList.propTypes = {
  item: PropTypes.shape({
    id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired,
    name: PropTypes.string.isRequired,
    selected: PropTypes.bool.isRequired,
    children: PropTypes.array,
    displayTag: PropTypes.string,
  }).isRequired,
  depth: PropTypes.number,
  handleSelect: PropTypes.func.isRequired,
  expandedDepth: PropTypes.number,
};

export default NestedList;
